import { Alert, Box, Stack, Tooltip, Typography } from "@mui/material"
import { Replay } from "@features/lead-routing/leadRouting.types"
import { SplitBars, SplitBarRow } from "./splitBars"

// Shared "Preview against recent leads" result view, aligned columns, the fill
// bar coloured by how far off target the consultant lands, and a black target
// marker. Used by both the rule card and the rule editor.
export function ReplayResultView(props: {
  replay: Replay
  consultantName: (id: string) => string
  showTitle?: boolean
}) {
  const { replay } = props
  const showTitle = props.showTitle ?? true

  if (replay.matched === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No leads in the last 30 days match this rule’s conditions.
      </Typography>
    )
  }

  const assigned = replay.matched - replay.noEligibleMatched
  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        mb={1}
      >
        {showTitle ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textTransform: "uppercase", letterSpacing: ".05em" }}
          >
            Preview · last 30 days
          </Typography>
        ) : (
          <span />
        )}
        <Tooltip title="A preview, not real results. It replays this rule over the last 30 days of leads. Each consultant shows the goal share you set, then the share that replay would give them once eligibility is applied (a consultant only takes leads they can accept).">
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ cursor: "help" }}
          >
            {replay.matched} matched · {assigned} assigned
          </Typography>
        </Tooltip>
      </Stack>

      <SplitBars
        rows={replay.distribution.map((row): SplitBarRow => {
          const drift = Math.round((row.simulatedPct - row.targetPct) * 10) / 10
          return {
            id: row.consultantId,
            name: props.consultantName(row.consultantId),
            targetPct: row.targetPct,
            actualPct: row.simulatedPct,
            barTooltip: `Preview, not real results. You set a goal of ${row.targetPct}% for them; replaying the last 30 days of leads through this rule would give them ${row.simulatedPct}%.`,
            deltaTooltip:
              drift === 0
                ? "Exactly on target."
                : `${Math.abs(drift)} percentage points ${
                    drift > 0 ? "above" : "below"
                  } their target share.`,
          }
        })}
      />

      {replay.noEligibleMatched > 0 && (
        <Alert severity="warning" sx={{ mt: 1.5, py: 0 }}>
          {replay.noEligibleMatched} lead
          {replay.noEligibleMatched > 1 ? "s" : ""} matched but no consultant in
          this split accepts their QTF, they’d fall through to Everyone else.
        </Alert>
      )}
    </Box>
  )
}
